import { FormEvent, useTransition } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { settleRedirect } from "@/lib/redirect-error";
import { acceptInvite } from "@/api/actions/org";

/**
 * 수락 폼. 이름과 비밀번호만 받는다 — 이메일·권한은 초대가 이미 정했다.
 * 비밀번호는 **이 자리에서 Supabase 계정이 된다**(초대 이메일이 아이디).
 *
 * 성공하면 액션이 그 계정으로 로그인시키고 `/app` 으로 **던진다**.
 * 그래서 catch 는 settleRedirect 로 이동을 먼저 거른다(RpcRedirect 는 조용히 끝 · /invite 는 로그인 없는 화면이라 401 은 세션·이동 없이 「합류하지 못했습니다」 토스트 · 404 는 다시 던짐) —
 * 삼키면 합류는 됐는데 「실패했습니다」 가 뜬다.
 */
const InviteForm = ({ token }: { token: string }) => {
  const [pending, startTransition] = useTransition();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    startTransition(async () => {
      try {
        const result = await acceptInvite(token, form);
        if (!result.ok) toast.error(result.message);
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error("합류하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <form className="flex flex-col gap-y-4" onSubmit={onSubmit}>
      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="invite-name">이름</Label>
        <Input
          autoComplete="name"
          disabled={pending}
          id="invite-name"
          maxLength={40}
          name="name"
          placeholder="홍길동"
          required
        />
      </div>

      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="invite-password">비밀번호</Label>
        <Input
          autoComplete="new-password"
          disabled={pending}
          id="invite-password"
          minLength={8}
          name="password"
          placeholder="8자 이상"
          required
          type="password"
        />
        <p className="text-xs text-muted-foreground">
          다음부터 이 비밀번호로 로그인합니다. 아이디는 초대받은 이메일입니다.
        </p>
      </div>

      <Button className="w-full" disabled={pending} type="submit">
        {pending ? "합류하는 중…" : "합류하기"}
      </Button>
    </form>
  );
};

export default InviteForm;
